import { syncBoardPresentations } from "@/config/boardPresentation";
import {
  type BoardRuntimeConfig,
  getBoardPublicPath,
} from "@/config/boardRuntimeConfig";
import {
  parseBoardRuntimeConfig,
  resolveInjectedBoardRuntimeConfig,
} from "@/config/developmentBoardRuntime";
import { formatBoardDisplayName } from "@/config/formatBoardDisplayName";

const NON_BROWSER_CONFIG_SYMBOL: symbol = Symbol.for(
  "nijiurachan.test.board-runtime-config",
);

const config: BoardRuntimeConfig | null =
  resolveBrowserBoardRuntimeConfig() ?? readNonBrowserTestConfig();
if (!config) {
  throw new Error("This path is not configured for a board");
}

export const BOARD_CONFIG: BoardRuntimeConfig = config;
syncBoardPresentations(
  Object.entries(config.mix?.boardPresentations ?? {}).map(
    ([slug, presentation]) => ({ slug, presentation }),
  ),
);
export const BOARD_DISPLAY_NAME: string = formatBoardDisplayName(BOARD_CONFIG);

/**
 * 個別板を指す面(スレページ)の表示名。mixでもアンカー板の `{板名}@あいもげ` を
 * 出すため、総合名の `BOARD_DISPLAY_NAME` と異なる(非mixでは同じ値)。
 */
export const ANCHOR_BOARD_DISPLAY_NAME: string = formatBoardDisplayName(
  BOARD_CONFIG,
  "board",
);

/** `/mix` 総合カタログとして起動しているか。 */
export const IS_MIX_CATALOG: boolean = BOARD_CONFIG.mix !== undefined;

/** 総合カタログ対象の登録済みslug(ラインナップ順、先頭=アンカー板)。非mixでは空。 */
export const MIX_REGISTERED_SLUGS: readonly string[] =
  BOARD_CONFIG.mix?.registeredSlugs ?? [];

/** 登録板のslug→板名。省略可能フィールドのため欠落し得る。非mixでは空。 */
export const MIX_BOARD_NAMES: Readonly<Record<string, string>> =
  BOARD_CONFIG.mix?.boardNames ?? {};

export function boardPublicPath(pathname: string): string {
  return getBoardPublicPath(BOARD_CONFIG, pathname);
}

/**
 * Read the config validated and injected by the delivery/runtime layer.
 * The shared bundle must not compare it with build-time API configuration.
 */
export function resolveBrowserBoardRuntimeConfig(
  documentLike:
    | Pick<Document, "getElementById">
    | undefined = globalThis.document,
  locationLike:
    | Pick<Location, "origin" | "pathname">
    | undefined = globalThis.location,
): BoardRuntimeConfig | null {
  return locationLike === undefined
    ? null
    : resolveInjectedBoardRuntimeConfig(
        documentLike,
        locationLike.pathname,
        locationLike.origin,
      );
}

function readNonBrowserTestConfig(): BoardRuntimeConfig | null {
  if (
    typeof globalThis.document !== "undefined" ||
    typeof globalThis.location !== "undefined"
  ) {
    return null;
  }
  return parseBoardRuntimeConfig(
    Reflect.get(globalThis, NON_BROWSER_CONFIG_SYMBOL),
  );
}
